// src/hooks/usePWA.js
// Custom React hook for PWA functionality

import { useState, useEffect, useRef } from 'react';
import offlineService from '../services/offlineService';
import { applyUpdateOnNavigation, isUpdateWaiting } from '../utils/serviceWorkerRegistration';

export const usePWA = (pathname) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is running in standalone mode
    const checkStandalone = () => {
      setIsStandalone(
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
      );
    };

    checkStandalone();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkStandalone);

    // Network status listeners
    const handleOnline = () => {
      setIsOnline(true);
      console.log('App is online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('App is offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // PWA install prompt listener
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // App installed listener
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', checkStandalone);
    };
  }, []);

  const installApp = async () => {
    if (installPrompt) {
      try {
        const result = await installPrompt.prompt();
        if (result.outcome === 'accepted') {
          setIsInstalled(true);
          setIsInstallable(false);
        }
        setInstallPrompt(null);
        return result.outcome;
      } catch (error) {
        console.error('Error installing app:', error);
        return 'error';
      }
    }
    return 'not-available';
  };

  // A downloaded update is applied on the next in-app navigation to a page that
  // is safe to reload — never on first render and never on its own.
  const lastPathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathname === lastPathnameRef.current) return;
    lastPathnameRef.current = pathname;
    applyUpdateOnNavigation(pathname);
  }, [pathname]);

  const updateApp = () => {
    if (isUpdateWaiting()) applyUpdateOnNavigation(window.location.pathname);
  };

  const shareApp = async (data = {}) => {
    const shareData = {
      title: 'Deutsch Prep App',
      text: 'Lernen Sie Deutsch mit der besten Prüfungsvorbereitung!',
      url: window.location.origin,
      ...data
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return 'shared';
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
        return 'error';
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareData.url);
        return 'copied';
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        return 'error';
      }
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission;
    }
    return 'not-supported';
  };

  const showNotification = (title, options = {}) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        action: 'show-notification',
        title,
        options: {
          icon: '/icons/manifest-icon-192.maskable.png',
          badge: '/icons/manifest-icon-192.maskable.png',
          ...options
        }
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        icon: '/icons/manifest-icon-192.maskable.png',
        ...options
      });
    }
  };

  return {
    isOnline,
    isOffline: !isOnline,
    installPrompt,
    isInstallable,
    isInstalled,
    isStandalone,
    updateAvailable: false, // updates apply on navigation, no prompt
    installApp,
    updateApp,
    shareApp,
    requestNotificationPermission,
    showNotification
  };
};

export const useOfflineData = () => {
  const [offlineStats, setOfflineStats] = useState(null);
  const [isDataSyncing, setIsDataSyncing] = useState(false);

  useEffect(() => {
    loadOfflineStats();
  }, []);

  const loadOfflineStats = async () => {
    try {
      const stats = await offlineService.getOfflineStats();
      setOfflineStats(stats);
    } catch (error) {
      console.error('Error loading offline stats:', error);
    }
  };

  const syncData = async () => {
    setIsDataSyncing(true);
    try {
      await offlineService.syncOfflineData();
      await loadOfflineStats();
    } catch (error) {
      console.error('Error syncing data:', error);
    } finally {
      setIsDataSyncing(false);
    }
  };

  const clearOfflineData = async () => {
    try {
      await offlineService.clearOfflineData();
      await loadOfflineStats();
    } catch (error) {
      console.error('Error clearing offline data:', error);
    }
  };

  const saveDataOffline = async (type, data) => {
    try {
      switch (type) {
        case 'userProgress':
          return await offlineService.saveUserProgressOffline(data.userId, data.progressData);
        case 'examResult':
          return await offlineService.saveExamResultOffline(data.userId, data.examId, data.resultData);
        case 'vocabulary':
          return await offlineService.saveVocabularyOffline(data.userId, data.vocabularyData);
        case 'studyPlan':
          return await offlineService.saveStudyPlanOffline(data.userId, data.planData);
        default:
          throw new Error('Unknown data type');
      }
    } catch (error) {
      console.error('Error saving data offline:', error);
      throw error;
    }
  };

  const getDataOffline = async (type, params) => {
    try {
      switch (type) {
        case 'userProgress':
          return await offlineService.getUserProgressOffline(params.userId);
        case 'examResults':
          return await offlineService.getExamResultsOffline(params.userId);
        case 'vocabulary':
          return await offlineService.getVocabularyOffline(params.userId, params.level);
        case 'studyPlans':
          return await offlineService.getStudyPlansOffline(params.userId);
        default:
          throw new Error('Unknown data type');
      }
    } catch (error) {
      console.error('Error getting data offline:', error);
      throw error;
    }
  };

  const isContentAvailableOffline = async (contentType, contentId) => {
    try {
      return await offlineService.isContentAvailableOffline(contentType, contentId);
    } catch (error) {
      console.error('Error checking offline content:', error);
      return false;
    }
  };

  return {
    offlineStats,
    isDataSyncing,
    syncData,
    clearOfflineData,
    saveDataOffline,
    getDataOffline,
    isContentAvailableOffline,
    loadOfflineStats
  };
};

export const useNetworkAwareData = (fetchFunction, fallbackFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const { isOnline } = usePWA();

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (isOnline) {
          // Try to fetch from network first
          try {
            const networkData = await fetchFunction();
            if (isMounted) {
              setData(networkData);
              setIsFromCache(false);
              setLoading(false);
            }
          } catch (networkError) {
            console.log('Network fetch failed, trying cache:', networkError);

            // Fallback to cached data
            if (fallbackFunction) {
              const cachedData = await fallbackFunction();
              if (isMounted) {
                setData(cachedData);
                setIsFromCache(true);
                setLoading(false);
              }
            } else {
              throw networkError;
            }
          }
        } else {
          // Offline - use cached data only
          if (fallbackFunction) {
            const cachedData = await fallbackFunction();
            if (isMounted) {
              setData(cachedData);
              setIsFromCache(true);
              setLoading(false);
            }
          } else {
            throw new Error('No offline data available');
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [isOnline, ...dependencies]);

  const refetch = async () => {
    setLoading(true);
    setError(null);

    try {
      const networkData = await fetchFunction();
      setData(networkData);
      setIsFromCache(false);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    isFromCache,
    refetch
  };
};